import { useCallback } from 'react'
import { useCurrentOrganization } from '@/stores/organization.store'
import { OrganizationFeatureEnum } from '@/gen/cortado/v1/settings_pb'
import type { OrganizationFeature } from '@/gen/cortado/v1/organizations_pb'

/**
 * True when a grant carries an expiry that has already passed.
 *
 * Mirrors the backend's expiry check in organization.Service.HasFeature — it
 * treats a lapsed grant as disabled, so without this the UI would render a
 * gated tab whose RPCs the backend then rejects with PermissionDenied, leaving
 * the user on a panel that can never load. An absent expires_at means the grant
 * never expires. An unparseable one is treated as no expiry so a bad timestamp
 * hides UI the org is otherwise entitled to.
 */
export function isFeatureExpired(feature: OrganizationFeature): boolean {
  const { expiresAt } = feature
  if (!expiresAt) return false

  // Proto timestamps carry seconds as a bigint; mirror date-utils' handling.
  const seconds =
    typeof expiresAt.seconds === 'bigint' ? Number(expiresAt.seconds) : expiresAt.seconds
  if (seconds == null || Number.isNaN(seconds)) return false

  return seconds * 1000 <= Date.now()
}

function organizationHasFeature(
  organization: { enabledFeatures?: OrganizationFeature[] } | null | undefined,
  feature: OrganizationFeatureEnum
): boolean {
  return (
    organization?.enabledFeatures?.some(
      (f) => f.feature === feature && f.enabled && !isFeatureExpired(f)
    ) ?? false
  )
}

/**
 * Hook to check if the current organization has a specific feature enabled
 *
 * @param feature - The feature to check for
 * @returns { hasFeature, isLoading, hasLoaded }
 */
export function useHasFeature(feature: OrganizationFeatureEnum) {
  const { currentOrganization, isLoading, hasLoaded } = useCurrentOrganization()

  return {
    hasFeature: organizationHasFeature(currentOrganization, feature),
    isLoading,
    hasLoaded,
  }
}

/**
 * Same entitlement check as useHasFeature, for callers that resolve a
 * dynamically sized list of features (the onboarding wizard's screen registry)
 * and so cannot call the single-feature hook once per entry.
 */
export function useHasFeatures(features: readonly OrganizationFeatureEnum[]) {
  const { currentOrganization, isLoading, hasLoaded } = useCurrentOrganization()

  const hasFeature = useCallback(
    (feature: OrganizationFeatureEnum) => organizationHasFeature(currentOrganization, feature),
    [currentOrganization]
  )

  return {
    hasFeature,
    enabled: features.filter(hasFeature),
    isLoading,
    hasLoaded,
  }
}

/**
 * Convenience hook specifically for the AI agent feature
 */
export function useHasAIAgentFeature() {
  return useHasFeature(OrganizationFeatureEnum.ORGANIZATION_FEATURE_AI_AGENT)
}

/**
 * Convenience hook specifically for AI agent autopilot (auto-execution of write actions)
 */
export function useHasAIAgentAutopilotFeature() {
  return useHasFeature(OrganizationFeatureEnum.ORGANIZATION_FEATURE_AI_AGENT_AUTOPILOT)
}

/**
 * Convenience hook specifically for email inbox feature
 */
export function useHasEmailInboxFeature() {
  return useHasFeature(OrganizationFeatureEnum.ORGANIZATION_FEATURE_EMAIL_INBOX)
}

/**
 * Convenience hook for tenant API keys and the external HTTP API
 */
export function useHasAPIAccessFeature() {
  return useHasFeature(OrganizationFeatureEnum.ORGANIZATION_FEATURE_API_ACCESS)
}

/**
 * Convenience hook for the Jobs list, the first Jobs UI surface.
 */
export function useHasJobsFeature() {
  return useHasFeature(OrganizationFeatureEnum.ORGANIZATION_FEATURE_JOBS)
}

/**
 * Convenience hook for lead referrals. A referral reaches another business
 * only when both have the feature.
 */
export function useHasLeadReferralsFeature() {
  return useHasFeature(OrganizationFeatureEnum.ORGANIZATION_FEATURE_LEAD_REFERRALS)
}
